import { useState } from 'react';

import {
    candidateKey,
    candidateToNewProperty,
    propertyApi,
    splitHouseNumber,
} from '@/entities/property';
import { useAddressLookup, type AddressLookup } from '@/shared/ui';

export interface ObjectAddress extends AddressLookup {
    /** De uuid van het object: het bestaande, of een nieuw met alles wat de BAG weet. */
    ensureProperty: () => Promise<string>;
}

/**
 * Het object van een nieuwe keuring: postcode en huisnummer in, het adres en de
 * gegevens van het pand uit de BAG terug ({@link useAddressLookup}, dezelfde
 * zoektocht als bij het adres van de klant).
 *
 * Het object wordt pas bij het aanmaken van de keuring gemaakt, en maar één
 * keer: een tweede poging na een mislukte keuring hergebruikt het, zolang het
 * adres niet veranderde.
 */
export function useObjectAddress(): ObjectAddress {
    const address = useAddressLookup();
    const [made, setMade] = useState<{ identity: string; uuid: string } | null>(null);

    /* Wat het object bepaalt: het gekozen BAG-adres, of het adres zoals het getypt is. */
    const identity =
        address.selected && !address.manual
            ? `bag:${candidateKey(address.selected)}`
            : `typed:${JSON.stringify([address.postcode, address.houseNumber, address.suffix, address.street, address.city])}`;

    async function typedProperty(): Promise<string> {
        const typed = splitHouseNumber(address.houseNumber, address.suffix);
        const known = await propertyApi.findByAddress({
            postcode: address.postcode,
            houseNumber: typed.houseNumber,
            suffix: typed.suffix,
        });

        if (known) {
            return known.uuid;
        }

        const created = await propertyApi.create({
            postcode: address.postcode,
            street: address.street.trim(),
            houseNumber: typed.houseNumber,
            houseNumberSuffix: typed.suffix,
            city: address.city.trim(),
        });

        return created.uuid;
    }

    return {
        ...address,
        ensureProperty: async () => {
            if (made?.identity === identity) {
                return made.uuid;
            }

            const uuid =
                address.selected && !address.manual
                    ? (address.selected.propertyUuid ??
                      (await propertyApi.create(candidateToNewProperty(address.selected))).uuid)
                    : await typedProperty();

            setMade({ identity, uuid });

            return uuid;
        },
    };
}
